"use client";

// What the signed-in person's netti wears beyond the code's mint: the live mint
// bands and a founder's hand-tuned body (resolved against their seed), plus the
// curated palette a founder gave them in place of their minted one. Remembered
// per seed so the NEXT visit's first frame already wears it; the fetch then
// confirms or corrects it.

import { useEffect, useState } from "react";
import type { ColorState } from "../organism/color";
import { resolveBody, sanitizeBands, sanitizeColor, sanitizeTuners, type TunerPatch } from "./tuner";

const KEY = "innernet:netti-body";

export type NettiWear = { body?: TunerPatch; color?: ColorState };

function remembered(seed: number): NettiWear {
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return {};
    const j = JSON.parse(raw) as { seed?: number; body?: unknown; color?: unknown };
    if (j.seed !== seed) return {};
    const body = j.body ? sanitizeTuners(j.body) : null;
    const color = sanitizeColor(j.color);
    return {
      ...(body && Object.keys(body).length ? { body } : {}),
      ...(color ? { color } : {}),
    };
  } catch {
    return {};
  }
}

export function useNettiBody(seed: number | null | undefined, enabled = true): NettiWear {
  const [wear, setWear] = useState<NettiWear>({});

  useEffect(() => {
    if (!enabled || seed == null) return;
    setWear(remembered(seed));
    let alive = true;
    void fetch("/api/me/netti")
      .then((r) => (r.ok ? r.json() : null))
      .then((j: { bands?: unknown; override?: unknown; color?: unknown } | null) => {
        if (!alive || !j) return;
        const bands = j.bands ? sanitizeBands(j.bands) : null;
        const override = j.override ? sanitizeTuners(j.override) : null;
        const body = resolveBody(seed, bands, override);
        const color = sanitizeColor(j.color);
        setWear({ ...(body ? { body } : {}), ...(color ? { color } : {}) });
        try {
          if (body || color) window.localStorage.setItem(KEY, JSON.stringify({ seed, body, color }));
          else window.localStorage.removeItem(KEY);
        } catch {
          /* private window — the fetch still dressed this session */
        }
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [seed, enabled]);

  return wear;
}
